import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { useAcList, useAcSummary, type PassStatus } from "../api/accessControlApi";
import { ConsolePage, Initials, KpiRow, KpiTile, LoadState, PASS_TONE, Panel, StatusChip, ToolButton, formatDateTime, relativeTime, toneOf, useTicker } from "../components/console/Console";
import { chartGrid, chartTick, chartTooltip } from "./SecurityDashboard";
import { EventRow } from "./AccessDashboard";
import { NUVEX_ACCESS_BASE } from "../paths";

// Accesos temporales → Tablero: vigentes, próximas visitas y lecturas QR.

const STATUSES: PassStatus[] = ["active", "scheduled", "used", "expired", "revoked"];
const TONE_COLOR = { success: "--c-success", info: "--c-info", primary: "--c-primary", neutral: "--c-content-faint", error: "--c-error", warning: "--c-warning" } as const;

export default function PassesDashboard() {
  const { t, i18n } = useTranslation("accessConsole");
  const navigate = useNavigate();
  const now = useTicker();
  const summaryQ = useAcSummary();
  const passesQ = useAcList("passes");
  const qrQ = useAcList("events", { method: "qr", limit: 10 });
  const s = summaryQ.data;

  const passes = useMemo(() => passesQ.data ?? [], [passesQ.data]);
  const upcoming = passes.filter((p) => p.effective_status === "scheduled" || p.effective_status === "active").sort((a, b) => Date.parse(a.valid_from) - Date.parse(b.valid_from)).slice(0, 8);
  const chart = STATUSES.map((st) => ({ key: st, name: t(`passStatus.${st}`), value: s?.passes.byStatus[st] ?? 0 }));

  return (
    <ConsolePage icon="ri-qr-code-line" title={t("passes.dashboardTitle")} subtitle={t("passes.dashboardSubtitle")} actions={<ToolButton icon="ri-add-line" variant="primary" onClick={() => navigate(`${NUVEX_ACCESS_BASE}/accesos-temporales/pases`)}>{t("passes.new")}</ToolButton>}>
      <LoadState loading={summaryQ.isLoading} error={summaryQ.error} empty={false} onRetry={() => summaryQ.refetch()}>
        {s && (
          <KpiRow>
            <KpiTile icon="ri-checkbox-circle-line" tone="success" label={t("passStatus.active")} value={s.passes.byStatus.active} />
            <KpiTile icon="ri-calendar-event-line" tone="info" label={t("passStatus.scheduled")} value={s.passes.byStatus.scheduled} />
            <KpiTile icon="ri-user-received-2-line" tone="primary" label={t("passes.expectedToday")} value={s.passes.expectedToday} />
            <KpiTile icon="ri-door-open-line" tone="primary" label={t("passStatus.used")} value={s.passes.byStatus.used} />
            <KpiTile icon="ri-time-line" tone="neutral" label={t("passStatus.expired")} value={s.passes.byStatus.expired} />
            <KpiTile icon="ri-forbid-line" tone="error" label={t("passStatus.revoked")} value={s.passes.byStatus.revoked} />
          </KpiRow>
        )}
      </LoadState>
      <div className="grid gap-4 xl:grid-cols-2">
        <Panel title={t("passes.upcoming")} icon="ri-calendar-check-line" bodyClassName="max-h-[420px] overflow-y-auto">
          <LoadState loading={passesQ.isLoading} error={passesQ.error} empty={!upcoming.length} emptyText={t("passes.empty")}>
            <ul className="divide-y divide-line">
              {upcoming.map((p) => (
                <li key={p.id} className="flex items-center gap-3 px-3 py-2.5">
                  <Initials name={p.visitor_name} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-content">{p.visitor_name}</p>
                    <p className="truncate text-xs text-content-secondary">{p.reason} · {t("passes.hostShort", { interpolation: { escapeValue: false }, host: p.host_name })}</p>
                    <p className="text-[11px] text-content-muted">{formatDateTime(p.valid_from, i18n.language)} · {p.site_name}</p>
                  </div>
                  <div className="text-right">
                    <StatusChip tone={toneOf(PASS_TONE, p.effective_status)}>{t(`passStatus.${p.effective_status}`)}</StatusChip>
                    <p className="mt-0.5 text-[11px] text-content-muted">{relativeTime(p.effective_status === "scheduled" ? p.valid_from : p.valid_until, i18n.language, now)}</p>
                  </div>
                </li>
              ))}
            </ul>
          </LoadState>
        </Panel>
        <div className="flex flex-col gap-4">
          <Panel title={t("passes.byStatus")} icon="ri-bar-chart-horizontal-line">
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={chartTick} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={chartTick} axisLine={false} tickLine={false} width={90} />
                  <Tooltip {...chartTooltip} />
                  <Bar dataKey="value" name={t("passes.total")} radius={[0, 4, 4, 0]}>
                    {chart.map((c) => <Cell key={c.key} fill={`rgb(var(${TONE_COLOR[toneOf(PASS_TONE, c.key)]}))`} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
          <Panel title={t("passes.recentQr")} icon="ri-qr-scan-2-line" bodyClassName="max-h-64 overflow-y-auto">
            <LoadState loading={qrQ.isLoading} error={qrQ.error} empty={!qrQ.data?.length} emptyText={t("events.empty")}>
              <ul className="divide-y divide-line">{(qrQ.data ?? []).slice(0, 10).map((e) => <EventRow key={e.id} e={e} now={now} compact />)}</ul>
            </LoadState>
          </Panel>
        </div>
      </div>
    </ConsolePage>
  );
}
